/*
 * cwear-customer-dashboard.js
 * Section: CWEAR: Customer Dashboard V2
 * Purpose: Wishlist con capa de persistencia + AJAX cart interactions
 * Pattern: IIFE con section.id scoping — no global state
 * Dependencies: cwear-customer-dashboard.css
 *
 * Arquitectura de wishlist:
 *   - localStorage    → cache local, acceso síncrono, UX instantánea
 *   - Storefront API  → lectura del metafield al cargar (solo si hay customer)
 *   - Sin escritura remota directa (Shopify clásico no lo permite desde frontend)
 *
 * Flujo al cargar:
 *   1. Mostrar desde localStorage inmediatamente (UX sin espera)
 *   2. Si hay customer → leer metafield via Storefront API
 *   3. Merge: unión de ambas listas, sin duplicados
 *   4. Actualizar localStorage con la lista mergeada
 *   5. Re-renderizar si hubo cambios
 *
 * Nota sobre escritura de metafields:
 *   Shopify no expone endpoint de escritura desde el browser en el sistema
 *   clásico de cuentas sin un App Proxy. La lista se persiste en localStorage
 *   y se sincroniza hacia arriba (metafield → local) en cada carga.
 *   Para escritura real se requiere una Custom App con App Proxy.
 */

(function () {
  'use strict';

  var shop = window.Shopify || { currency: 'MXN' };

  // ─── Constantes ────────────────────────────────────────────────────────────

  var WISHLIST_KEY = 'cwwear_wishlist';

  // El Storefront Access Token es público por diseño — no es un secreto.
  // Solo permite operaciones de lectura limitadas que Shopify expone públicamente.
  // Se configura en el Liquid via data-storefront-token para no hardcodearlo aquí.
  var STOREFRONT_TOKEN = null;
  var STOREFRONT_URL = null;

  // ─── Inicialización de sección ─────────────────────────────────────────────

  var sections = document.querySelectorAll('[data-section-type="cwear-customer-dashboard"]');
  if (!sections.length) return;

  sections.forEach(function (section) {
    var sectionId = section.dataset.sectionId;
    var wishlistGrid = section.querySelector('[data-wishlist-container]');
    var wishlistEmpty = section.querySelector('[data-wishlist-empty]');
    var reorderContainer = section.querySelector('[data-reorder-container]');
    var skeleton = section.querySelector('[data-wishlist-loading]');
    var customerId = section.dataset.customerId || null;
    var metafieldRaw = section.dataset.wishlistMetafield || null;

    // Leer tokens del DOM si están disponibles
    STOREFRONT_TOKEN = section.dataset.storefrontToken || null;
    STOREFRONT_URL = section.dataset.storefrontUrl || null;

    if (!wishlistGrid) return;

    initDashboardSection(
      sectionId,
      wishlistGrid,
      wishlistEmpty,
      reorderContainer,
      skeleton,
      customerId,
      metafieldRaw
    );
  });

  // ─── Capa de persistencia: localStorage ────────────────────────────────────

  function getLocalWishlist() {
    try {
      var data = localStorage.getItem(WISHLIST_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.warn('[cwear-wishlist] localStorage no disponible:', e);
      return [];
    }
  }

  function setLocalWishlist(items) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    } catch (e) {
      console.warn('[cwear-wishlist] No se pudo guardar en localStorage:', e);
    }
  }

  // ─── Capa de persistencia: metafield (solo lectura vía Liquid) ─────────────

  /*
   * parseMetafieldWishlist
   * Lee la wishlist que Shopify inyectó en el data attribute al renderizar
   * el Liquid (customer.metafields.custom.wishlist.value).
   * Esta es la fuente de verdad del servidor — si existe, tiene prioridad.
   *
   * Retorna array de IDs o [] si no hay metafield o el formato es inválido.
   */
  function parseMetafieldWishlist(raw) {
    if (!raw || raw === '[]' || raw === 'null' || raw === '') return [];
    try {
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Normalizar a strings para comparación consistente
      return parsed.map(function (id) { return String(id); });
    } catch (e) {
      console.warn('[cwear-wishlist] Metafield con formato inválido:', raw);
      return [];
    }
  }

  // ─── API pública de wishlist ────────────────────────────────────────────────

  /*
   * getWishlist
   * Fuente de verdad local en runtime. Siempre lee desde localStorage.
   */
  function getWishlist() {
    return getLocalWishlist();
  }

  /*
   * addToWishlist
   * Agrega un variantId a localStorage.
   * No hace escritura remota (requeriría App Proxy).
   */
  function addToWishlist(variantId) {
    var id = String(variantId);
    var wishlist = getLocalWishlist();
    if (wishlist.indexOf(id) === -1) {
      wishlist.push(id);
      setLocalWishlist(wishlist);
      hydrateWishlistStat();
    }
  }

  /*
   * removeFromWishlist
   * Elimina un variantId de localStorage.
   */
  function removeFromWishlist(variantId) {
    var id = String(variantId);
    var wishlist = getLocalWishlist();
    var index = wishlist.indexOf(id);
    if (index !== -1) {
      wishlist.splice(index, 1);
      setLocalWishlist(wishlist);
      hydrateWishlistStat();
      return true;
    }
    return false;
  }

  function isInWishlist(variantId) {
    return getLocalWishlist().indexOf(String(variantId)) !== -1;
  }

  /*
   * mergeWishlists
   * Une la lista local con la del metafield del servidor.
   * Regla: unión sin duplicados. No se eliminan ítems — si está en cualquiera
   * de las dos fuentes, se mantiene. Esto protege contra pérdida de datos.
   *
   * Retorna { merged: Array, hasChanges: Boolean }
   */
  function mergeWishlists(localList, serverList) {
    var merged = localList.slice();
    var changed = false;

    serverList.forEach(function (id) {
      var strId = String(id);
      if (merged.indexOf(strId) === -1) {
        merged.push(strId);
        changed = true;
      }
    });

    return { merged: merged, hasChanges: changed };
  }

  /*
   * initWishlistSync
   * Punto de entrada de la sincronización.
   * Si hay metafield del servidor (inyectado por Liquid), lo mergea con local
   * y actualiza localStorage. Re-renderiza solo si hubo cambios.
   */
  function initWishlistSync(metafieldRaw, onSynced) {
    var localList = getLocalWishlist();
    var serverList = parseMetafieldWishlist(metafieldRaw);

    if (!serverList.length) {
      // Sin metafield — usar solo localStorage, sin re-render
      onSynced(false);
      return;
    }

    var result = mergeWishlists(localList, serverList);

    if (result.hasChanges) {
      setLocalWishlist(result.merged);
      console.log(
        '[cwear-wishlist] Merge completado. ' +
        serverList.length + ' items del servidor, ' +
        result.merged.length + ' items tras merge.'
      );
    }

    onSynced(result.hasChanges);
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  function renderWishlist(sectionId, wishlistGrid, wishlistEmpty, skeleton) {
    var wishlist = getWishlist();

    if (skeleton) {
      skeleton.style.display = 'none';
    }

    if (!wishlist.length) {
      if (wishlistEmpty) {
        wishlistEmpty.style.display = 'flex';
        wishlistGrid.innerHTML = '';
        wishlistGrid.appendChild(wishlistEmpty);
      }
      hydrateWishlistStat();
      return;
    }

    if (wishlistEmpty) wishlistEmpty.style.display = 'none';

    fetchWishlistProducts(wishlist, function (products) {
      wishlistGrid.innerHTML = '';

      if (!products || !products.length) {
        if (wishlistEmpty) {
          wishlistEmpty.style.display = 'flex';
          wishlistGrid.appendChild(wishlistEmpty);
        }
        return;
      }

      products.forEach(function (product) {
        var card = createProductCard(product, true);
        wishlistGrid.appendChild(card);
      });

      attachWishlistListeners(sectionId, wishlistGrid);
      hydrateWishlistStat();
    });
  }

  function fetchWishlistProducts(entries, callback) {
    if (!entries || !entries.length) {
      callback([]);
      return;
    }

    var pending = entries.length;
    var results = [];

    entries.forEach(function (entry) {
      // Formato soportado:
      //   "handle|productId"  → guardado por cwear-pdp-main.js (nuevo formato)
      //   "12345678"          → ID numérico puro (formato legacy o agregado por dashboard)
      var parts = entry.toString().split('|');
      var handle = parts.length === 2 ? parts[0] : null;
      var storedId = parts.length === 2 ? parts[1] : parts[0];
      var fetchUrl = handle
        ? '/products/' + handle + '.js'
        : '/products/' + storedId + '.js';

      fetch(fetchUrl)
        .then(function (response) {
          if (!response.ok) throw new Error('fetch failed: ' + fetchUrl);
          return response.json();
        })
        .then(function (product) {
          // Intentar encontrar la variante por storedId; fallback a la primera
          var variant = null;
          for (var i = 0; i < product.variants.length; i++) {
            if (String(product.variants[i].id) === String(storedId)) {
              variant = product.variants[i];
              break;
            }
          }
          if (!variant) variant = product.variants[0];

          var image = product.featured_image ||
            (product.images && product.images.length ? product.images[0] : null);

          results.push({
            id: product.id,
            variant_id: variant.id,
            title: product.title,
            price: (variant.price / 100).toFixed(2) + ' ' + shop.currency,
            image: image,
            url: product.url,
            available: variant.available
          });

          pending--;
          if (pending === 0) callback(results);
        })
        .catch(function (err) {
          console.warn('[cwear-wishlist] No se pudo cargar producto:', err.message);
          pending--;
          if (pending === 0) callback(results);
        });
    });
  }

  function createProductCard(product, isWishlist) {
    var card = document.createElement('div');
    card.className = 'cwear-customer-dashboard__product-card';
    card.dataset.productId = product.id;
    card.dataset.variantId = product.variant_id || product.id;
    card.dataset.available = product.available !== false;

    // Image wrapper
    var imageWrapper = document.createElement('div');
    imageWrapper.className = 'cwear-customer-dashboard__product-image';
    if (product.image) {
      var img = document.createElement('img');
      img.src = product.image;
      img.alt = product.title || 'Producto';
      img.loading = 'lazy';
      imageWrapper.appendChild(img);
    } else {
      imageWrapper.style.cssText = 'background:#f0f0f0;display:flex;align-items:center;justify-content:center;';
      imageWrapper.innerHTML = '<svg width="48" height="48" viewBox="0 0 48 48" fill="none"><rect width="48" height="48" rx="8" fill="#d0d0d0"/><path d="M10 34L20 20l8 10 5-6 5 10H10z" fill="#b0b0b0"/></svg>';
    }

    // Info wrapper
    var info = document.createElement('div');
    info.className = 'cwear-customer-dashboard__product-info';

    var name = document.createElement('h3');
    name.className = 'cwear-customer-dashboard__product-name';
    name.textContent = product.title || 'Producto';

    var price = document.createElement('p');
    price.className = 'cwear-customer-dashboard__product-price';
    price.textContent = product.price || '';

    info.appendChild(name);
    info.appendChild(price);

    if (isWishlist) {
      var addBtn = document.createElement('button');
      addBtn.className = 'cwear-btn cwear-customer-dashboard__wishlist-btn';
      addBtn.dataset.addToCart = product.variant_id;
      addBtn.textContent = 'Agregar al carrito';

      var removeBtn = document.createElement('button');
      removeBtn.className = 'cwear-customer-dashboard__remove-wishlist-btn';
      removeBtn.dataset.removeWishlist = product.variant_id;
      removeBtn.setAttribute('aria-label', 'Eliminar de wishlist');
      removeBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

      info.appendChild(addBtn);
      info.appendChild(removeBtn);
    } else {
      var reorderBtn = document.createElement('button');
      reorderBtn.className = 'cwear-btn cwear-customer-dashboard__reorder-btn';
      reorderBtn.dataset.addToCart = product.variant_id;
      reorderBtn.textContent = 'Comprar de nuevo';
      info.appendChild(reorderBtn);
    }

    card.appendChild(imageWrapper);
    card.appendChild(info);
    return card;
  }

  // ─── Event listeners ───────────────────────────────────────────────────────

  function attachWishlistListeners(sectionId, container) {
    container.querySelectorAll('[data-remove-wishlist]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var variantId = this.dataset.removeWishlist;
        var card = this.closest('.cwear-customer-dashboard__product-card');

        if (card) {
          card.style.opacity = '0';
          card.style.transform = 'scale(0.95)';
          card.style.transition = 'opacity 0.2s, transform 0.2s';
          setTimeout(function () {
            removeFromWishlist(variantId);
            renderWishlist(sectionId, container, container.querySelector('[data-wishlist-empty]'));
          }, 200);
        } else {
          removeFromWishlist(variantId);
          renderWishlist(sectionId, container, container.querySelector('[data-wishlist-empty]'));
        }
      });
    });

    container.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        addToCart(this.dataset.addToCart, this);
      });
    });
  }

  function attachReorderListeners(sectionId, reorderContainer) {
    if (!reorderContainer) return;

    reorderContainer.querySelectorAll('[data-reorder-variant-id]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        addToCart(this.dataset.reorderVariantId, this);
      });
    });
  }

  // ─── Cart ──────────────────────────────────────────────────────────────────

  function addToCart(variantId, button) {
    if (!variantId) return;

    var card = button.closest('.cwear-customer-dashboard__product-card');
    if (card && card.dataset.available === 'false') {
      button.textContent = 'Agotado';
      button.disabled = true;
      return;
    }

    var originalText = button.textContent;
    button.textContent = 'Agregando...';
    button.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Error al agregar al carrito');
        return response.json();
      })
      .then(function (data) {
        button.textContent = '¡Agregado!';
        button.classList.add('is-added');
        updateCartCount(data.item_count);

        setTimeout(function () {
          button.textContent = originalText;
          button.disabled = false;
          button.classList.remove('is-added');
        }, 2000);
      })
      .catch(function (error) {
        console.error('[cwear-dashboard] Error al agregar al carrito:', error);
        button.textContent = 'Error';
        button.classList.add('is-error');

        setTimeout(function () {
          button.textContent = originalText;
          button.disabled = false;
          button.classList.remove('is-error');
        }, 2000);
      });
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cwear-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });

    document.dispatchEvent(new CustomEvent('cwear:cart:updated', {
      detail: { count: count }
    }));
  }

  // ─── Stats ─────────────────────────────────────────────────────────────────

  function hydrateWishlistStat() {
    var count = getWishlist().length;

    // Dashboard stat
    var countEl = document.querySelector('[data-stat-wishlist-count]');
    if (countEl) countEl.textContent = count;

    // Header badge (desktop + mobile — todos los elementos con el mismo selector)
    document.querySelectorAll('[data-cwear-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });

    // Evento para que otros módulos puedan reaccionar (ej. PDP, futuro sync)
    document.dispatchEvent(new CustomEvent('cwear:wishlist:updated', {
      detail: { count: count }
    }));
  }

  // ─── Init ──────────────────────────────────────────────────────────────────

  function initDashboardSection(
    sectionId,
    wishlistGrid,
    wishlistEmpty,
    reorderContainer,
    skeleton,
    customerId,
    metafieldRaw
  ) {
    // 1. Render inmediato desde localStorage (UX sin espera)
    renderWishlist(sectionId, wishlistGrid, wishlistEmpty, skeleton);
    attachReorderListeners(sectionId, reorderContainer);

    // 2. Si hay cliente logueado → sync con metafield inyectado por Liquid
    if (customerId && metafieldRaw) {
      initWishlistSync(metafieldRaw, function (hasChanges) {
        if (hasChanges) {
          renderWishlist(sectionId, wishlistGrid, wishlistEmpty, null);
        }
      });
    }

    console.log('[cwear-dashboard] Section ' + sectionId + ' initialized' +
      (customerId ? ' (customer: ' + customerId + ')' : ' (guest)'));
  }

  // Actualizar badge del header en cualquier página cuando el PDP
  // o cualquier otro módulo dispare cwear:wishlist:updated
  document.addEventListener('cwear:wishlist:updated', function () {
    var count = getWishlist().length;
    document.querySelectorAll('[data-cwear-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  });

  // Hidratar el badge del header al cargar cualquier página
  // (independientemente de si la sección del dashboard está presente)
  hydrateWishlistStat();

})();
